import React from 'react';
import { PlusIcon } from '@heroicons/react/24/solid';
import { PitchSlot, ROLE_LABELS, shortName } from './formations';

export interface PitchPlayer {
  id: number;
  name: string;
  shirtNumber?: number;
}

// Ugyanazok a fűszínek, mint az edzés-rajzolóban
const GRASS_DARK = '#3f8f4d';
const GRASS_LIGHT = '#48a057';

const line = {
  fill: 'none',
  stroke: 'rgba(255,255,255,0.85)',
  strokeWidth: 0.35,
};

/** Álló, teljes pálya (68 x 105 m); a csapat felfelé támad. */
const PitchBackground: React.FC = () => (
  <svg viewBox="0 0 68 105" preserveAspectRatio="none" className="absolute inset-0 h-full w-full" aria-hidden="true">
    <rect x={0} y={0} width={68} height={105} fill={GRASS_LIGHT} />
    {Array.from({ length: 12 }, (_, i) =>
      i % 2 === 0 ? <rect key={i} x={0} y={(105 / 12) * i} width={68} height={105 / 12} fill={GRASS_DARK} /> : null
    )}
    <rect x={1.5} y={1.5} width={65} height={102} {...line} />
    <line x1={1.5} y1={52.5} x2={66.5} y2={52.5} {...line} />
    <circle cx={34} cy={52.5} r={9.15} {...line} />
    <circle cx={34} cy={52.5} r={0.5} fill="rgba(255,255,255,0.85)" />
    {/* Felső (ellenfél) kapu */}
    <rect x={34 - 20.16} y={1.5} width={40.32} height={16.5} {...line} />
    <rect x={34 - 9.16} y={1.5} width={18.32} height={5.5} {...line} />
    <path d={`M ${34 - 7.31} 18 A 9.15 9.15 0 0 0 ${34 + 7.31} 18`} {...line} />
    {/* Alsó (saját) kapu */}
    <rect x={34 - 20.16} y={103.5 - 16.5} width={40.32} height={16.5} {...line} />
    <rect x={34 - 9.16} y={103.5 - 5.5} width={18.32} height={5.5} {...line} />
    <path d={`M ${34 - 7.31} 87 A 9.15 9.15 0 0 1 ${34 + 7.31} 87`} {...line} />
  </svg>
);

/** Mez ikon a mezszámmal – a kapus eltérő színű, mint a valóságban. */
export const Jersey: React.FC<{ number?: number; keeper?: boolean; className?: string }> = ({ number, keeper, className }) => (
  <svg viewBox="0 0 40 40" className={className} aria-hidden="true">
    <path
      d="M13 4 L17 2.5 Q20 6 23 2.5 L27 4 L37 10 L33 18 L29 16 L29 37 Q20 39 11 37 L11 16 L7 18 L3 10 Z"
      fill={keeper ? '#f59e0b' : 'var(--color-primary-600)'}
      stroke="#ffffff"
      strokeWidth={2}
      strokeLinejoin="round"
    />
    <text
      x={20}
      y={23}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={number != null && number > 9 ? 13 : 15}
      fontWeight={800}
      fill="#ffffff"
      fontFamily="system-ui, -apple-system, sans-serif"
    >
      {number ?? ''}
    </text>
  </svg>
);

export const PlayerToken: React.FC<{
  player: PitchPlayer;
  role?: string;
  selected?: boolean;
  highlighted?: boolean;
  dimmed?: boolean;
  size?: 'sm' | 'md';
}> = ({ player, role, selected, highlighted, dimmed, size = 'md' }) => (
  <div className={`flex flex-col items-center transition-transform ${highlighted ? 'scale-110' : ''} ${dimmed ? 'opacity-30' : ''}`}>
    <div
      className={`relative rounded-full ${
        selected ? 'bg-yellow-300/40 ring-2 ring-yellow-300' : highlighted ? 'bg-white/30 ring-2 ring-white' : ''
      }`}
    >
      <Jersey
        number={player.shirtNumber}
        keeper={role === 'GK'}
        className={`drop-shadow-md ${size === 'sm' ? 'h-9 w-9' : 'h-10 w-10 sm:h-12 sm:w-12'}`}
      />
      {role && (
        <span className="absolute -right-2 -top-1 rounded bg-gray-900/85 px-1 text-[9px] font-bold leading-tight text-white">
          {role}
        </span>
      )}
    </div>
    <span
      className={`mt-0.5 truncate rounded py-px font-semibold leading-tight ${
        size === 'sm' ? 'max-w-[66px] px-1 text-[10px]' : 'max-w-[72px] px-1.5 text-[10px] sm:max-w-[88px] sm:text-xs'
      } ${selected ? 'bg-yellow-300 text-gray-900' : 'bg-gray-900/70 text-white'}`}
    >
      {shortName(player.name)}
    </span>
  </div>
);

const EmptySlot: React.FC<{ role: string; highlighted?: boolean; interactive?: boolean; size?: 'sm' | 'md' }> = ({
  role,
  highlighted,
  interactive,
  size = 'md',
}) => (
  <div className={`flex flex-col items-center transition-transform ${highlighted ? 'scale-110' : ''}`}>
    <div
      className={`flex items-center justify-center rounded-full border-2 border-dashed ${size === 'sm' ? 'h-9 w-9' : 'h-10 w-10 sm:h-12 sm:w-12'} ${
        highlighted ? 'border-white bg-white/35' : 'border-white/80 bg-white/10'
      }`}
    >
      {interactive && <PlusIcon className="h-5 w-5 text-white" />}
    </div>
    <span className="mt-0.5 rounded bg-gray-900/50 px-1.5 py-px text-[10px] font-semibold leading-tight text-white">{role}</span>
  </div>
);

interface FormationPitchProps {
  slots: PitchSlot[];
  players: Map<number, PitchPlayer>;
  interactive?: boolean;
  selectedSlotId?: string | null;
  dropTargetSlotId?: string | null;
  draggingSlotId?: string | null;
  onSlotPointerDown?: (e: React.PointerEvent, slot: PitchSlot) => void;
  onSlotClick?: (slot: PitchSlot) => void;
  pitchRef?: React.Ref<HTMLDivElement>;
  style?: React.CSSProperties;
  className?: string;
  /** Kis pályán kisebb kártyák, hogy a nevek ne csússzanak egymásra. */
  tokenSize?: 'sm' | 'md';
}

/**
 * Felállás-ábra. Csak megjelenít – a szerkesztő köti rá a húzást és a koppintást,
 * így ugyanez adja a csak olvasható nézetet és a szerkesztőt is.
 */
const FormationPitch: React.FC<FormationPitchProps> = ({
  slots,
  players,
  interactive,
  selectedSlotId,
  dropTargetSlotId,
  draggingSlotId,
  onSlotPointerDown,
  onSlotClick,
  pitchRef,
  style,
  className,
  tokenSize = 'md',
}) => (
  <div
    ref={pitchRef}
    data-drop="pitch"
    className={`relative overflow-hidden rounded-xl shadow-lg select-none ${className ?? ''}`}
    style={{ aspectRatio: '68 / 105', ...style }}
  >
    <PitchBackground />
    {slots.map((slot) => {
      const player = slot.playerId != null ? players.get(slot.playerId) : undefined;
      const label = player
        ? `${player.name}${player.shirtNumber != null ? `, ${player.shirtNumber}-es mez` : ''}, ${ROLE_LABELS[slot.role] ?? slot.role}`
        : `Üres hely: ${ROLE_LABELS[slot.role] ?? slot.role}`;
      const content = player ? (
        <PlayerToken
          player={player}
          role={slot.role}
          selected={selectedSlotId === slot.id}
          highlighted={dropTargetSlotId === slot.id}
          dimmed={draggingSlotId === slot.id}
          size={tokenSize}
        />
      ) : (
        <EmptySlot role={slot.role} highlighted={dropTargetSlotId === slot.id || selectedSlotId === slot.id} interactive={interactive} size={tokenSize} />
      );
      const position: React.CSSProperties = { left: `${slot.x}%`, top: `${slot.y}%` };
      return interactive ? (
        <button
          key={slot.id}
          type="button"
          data-drop={`slot:${slot.id}`}
          aria-label={label}
          title={label}
          onPointerDown={player && onSlotPointerDown ? (e) => onSlotPointerDown(e, slot) : undefined}
          onClick={onSlotClick ? () => onSlotClick(slot) : undefined}
          className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full p-1 outline-none focus-visible:ring-2 focus-visible:ring-white"
          style={{ ...position, touchAction: 'none' }}
        >
          {content}
        </button>
      ) : (
        <div key={slot.id} className="absolute -translate-x-1/2 -translate-y-1/2" style={position} title={label} aria-label={label} role="img">
          {content}
        </div>
      );
    })}
  </div>
);

export default FormationPitch;
